import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  output,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import type {
  ColumnAlign,
  ColumnWidths,
  DocumentItem,
  DocumentMapper,
  ExplorerView,
  SearchOptions,
  SelectionConfig,
  SortState,
} from '@document-explorer/core';
import {
  DEFAULT_INTERACTION,
  type ActionsConfig,
  type ColumnDef,
  type InteractionConfig,
  type ItemTemplateContext,
} from './types.js';
import { resolveColumns } from './columns.js';
import { resolveActions } from './actions.js';
import { ExplorerStoreService } from './explorer-store.js';
import { DeBreadcrumbsComponent } from './breadcrumbs.component.js';
import { DeSearchInputComponent } from './search-input.component.js';
import { DeViewSwitcherComponent } from './view-switcher.component.js';
import { DeListViewComponent } from './list-view.component.js';
import { DeGridViewComponent } from './grid-view.component.js';
import { DeTreeViewComponent } from './tree-view.component.js';
import { DeSelectionBarComponent } from './selection-bar.component.js';
import { DeEmptyStateComponent, DeErrorStateComponent, DeLoadingStateComponent } from './states.component.js';
import { DeColumnTemplateDirective } from './column-template.directive.js';

type SearchInput = boolean | (SearchOptions & { scope?: 'folder' | 'global'; placeholder?: string });

/**
 * Turn any document data into a clean, navigable explorer.
 *
 * ```html
 * <doc-explorer [data]="documents" [search]="true" [selection]="true" />
 * ```
 *
 * Every behaviour comes from `@document-explorer/core` — the same engine the
 * React package uses — so the two stay in lockstep by construction.
 */
@Component({
  selector: 'doc-explorer',
  standalone: true,
  providers: [ExplorerStoreService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    DeBreadcrumbsComponent,
    DeSearchInputComponent,
    DeViewSwitcherComponent,
    DeListViewComponent,
    DeGridViewComponent,
    DeTreeViewComponent,
    DeSelectionBarComponent,
    DeEmptyStateComponent,
    DeErrorStateComponent,
    DeLoadingStateComponent,
  ],
  host: { '[class]': 'hostClass()' },
  template: `
    @if (title() || searchEnabled() || availableViews().length > 1 || toolbarTemplate()) {
      <div class="de-header">
        @if (title()) {
          <h2 class="de-title">{{ title() }}</h2>
        } @else {
          <span></span>
        }
        <div class="de-header-actions">
          @if (searchEnabled()) {
            <de-search-input
              [value]="state().query"
              [placeholder]="searchPlaceholder()"
              (valueChange)="store.actions.setQuery($event)"
            />
          }
          @if (toolbarTemplate(); as toolbar) {
            <ng-container *ngTemplateOutlet="toolbar" />
          }
          <de-view-switcher
            [views]="availableViews()"
            [current]="state().view"
            (viewChange)="store.actions.setView($event)"
          />
        </div>
      </div>
    }

    @if (state().view !== 'tree') {
      @if (snapshot().isSearching && searchScope() === 'global') {
        <!-- A global search lists documents from other folders, so the
             breadcrumb alone would misdescribe what is on screen. -->
        <div class="de-breadcrumbs">
          <span class="de-crumb" aria-current="page">Search results for “{{ state().query }}”</span>
        </div>
      } @else {
        <de-breadcrumbs [items]="snapshot().breadcrumbs" (navigate)="store.actions.openFolder($event)" />
      }
    }

    <div class="de-content">
      @if (state().status === 'loading') {
        <de-loading-state />
      } @else if (state().status === 'error') {
        <de-error-state [error]="state().error" />
      } @else if (state().view === 'tree') {
        <de-tree-view
          [index]="snapshot().index"
          [cwd]="state().cwd"
          [selectedIds]="state().selection.ids"
          [selectionEnabled]="selectionConfig().enabled"
          (open)="store.actions.open($event)"
          (toggleSelect)="store.actions.toggleSelect($event)"
        />
      } @else if (snapshot().isEmpty) {
        @if (emptyTemplate(); as template) {
          <ng-container *ngTemplateOutlet="template" />
        } @else {
          <de-empty-state [isSearching]="snapshot().isSearching" [query]="state().query" />
        }
      } @else if (state().view === 'grid') {
        <de-grid-view
          [items]="snapshot().items"
          [actions]="resolvedActions()"
          [interaction]="resolvedInteraction()"
          [selectionConfig]="selectionConfig()"
          [selectedIds]="state().selection.ids"
          [locale]="locale()"
          [fileTemplate]="fileTemplate()"
          [folderTemplate]="folderTemplate()"
          (open)="store.actions.open($event)"
          (toggleSelect)="store.actions.toggleSelect($event)"
          (selectRange)="store.actions.selectRange($event)"
          (selectAll)="store.actions.selectAll()"
          (clearSelection)="store.actions.clearSelection()"
          (navigateUp)="store.actions.navigateUp()"
          (action)="onAction($event)"
        />
      } @else {
        <de-list-view
          [items]="snapshot().items"
          [columns]="resolvedColumns()"
          [sort]="state().sort"
          [actions]="resolvedActions()"
          [interaction]="resolvedInteraction()"
          [selectionConfig]="selectionConfig()"
          [selectedIds]="state().selection.ids"
          [locale]="locale()"
          [fileTemplate]="fileTemplate()"
          [folderTemplate]="folderTemplate()"
          [columnTemplates]="columnTemplates()"
          [resizable]="resizableColumns()"
          [columnWidths]="state().columnWidths"
          [columnAlign]="columnAlign()"
          (columnResize)="onColumnResize($event)"
          (columnResizeCommit)="store.actions.commitColumnResize($event)"
          (columnResizeReset)="store.actions.resetColumnWidth($event)"
          (sortChange)="store.actions.setSort($event)"
          (open)="store.actions.open($event)"
          (toggleSelect)="store.actions.toggleSelect($event)"
          (selectRange)="store.actions.selectRange($event)"
          (selectAll)="store.actions.selectAll()"
          (clearSelection)="store.actions.clearSelection()"
          (navigateUp)="store.actions.navigateUp()"
          (action)="onAction($event)"
        />
      }
    </div>

    <!-- One live region for the whole explorer, so a screen reader hears the
         result of navigating or searching without focus being moved. -->
    <span class="de-sr-only" role="status" aria-live="polite">{{ announcement() }}</span>

    <de-selection-bar
      [selected]="snapshot().selectedItems"
      [canSelectAll]="selectionConfig().mode === 'multiple'"
      (clear)="store.actions.clearSelection()"
      (selectAll)="store.actions.selectAll()"
    />
  `,
})
export class DocumentExplorerComponent<T = DocumentItem> {
  protected readonly store = inject(ExplorerStoreService);

  // --- inputs -----------------------------------------------------------
  readonly data = input<readonly T[] | undefined>(undefined);
  readonly mapper = input<DocumentMapper<T> | undefined>(undefined);
  readonly title = input<string | undefined>(undefined);
  readonly rootLabel = input<string>('Home');
  readonly view = input<ExplorerView | undefined>(undefined);
  readonly views = input<ExplorerView[] | undefined>(undefined);
  readonly defaultView = input<ExplorerView>('list');
  readonly search = input<SearchInput>(false);
  readonly sort = input<Partial<SortState> | undefined>(undefined);
  readonly columns = input<(ColumnDef | string)[] | undefined>(undefined);
  /**
   * Alignment for every column that does not set its own. Default `'left'`,
   * so the table is consistent unless you say otherwise.
   */
  readonly columnAlign = input<ColumnAlign | undefined>(undefined);
  /** Let the user drag column edges. Off by default. */
  readonly resizableColumns = input<boolean>(false);
  /** Starting widths in pixels, e.g. restored from a previous session. */
  readonly columnWidths = input<ColumnWidths | undefined>(undefined);
  readonly selection = input<boolean | Partial<SelectionConfig>>(false);
  readonly actions = input<ActionsConfig | undefined>(undefined);
  readonly interaction = input<InteractionConfig | undefined>(undefined);
  readonly loading = input<boolean>(false);
  readonly error = input<unknown>(undefined);
  readonly locale = input<string | undefined>(undefined);
  readonly className = input<string>('');

  // --- content slots ----------------------------------------------------
  readonly fileTemplate = contentChild<TemplateRef<ItemTemplateContext>>('deFile');
  readonly folderTemplate = contentChild<TemplateRef<ItemTemplateContext>>('deFolder');
  readonly emptyTemplate = contentChild<TemplateRef<unknown>>('deEmpty');
  readonly toolbarTemplate = contentChild<TemplateRef<unknown>>('deToolbar');
  /** Per-column cell templates — the Angular equivalent of `ColumnDef.render`. */
  readonly columnTemplates = contentChildren(DeColumnTemplateDirective);

  // --- outputs ----------------------------------------------------------
  readonly folderOpen = output<DocumentItem | null>();
  readonly documentOpen = output<DocumentItem>();
  readonly selectionChange = output<DocumentItem[]>();
  readonly searchChange = output<string>();
  readonly sortChange = output<SortState>();
  readonly viewChange = output<ExplorerView>();
  readonly action = output<{ actionId: string; item: DocumentItem }>();
  readonly documentView = output<DocumentItem>();
  readonly documentDownload = output<DocumentItem>();
  /** Fires once per resize, on release — the right moment to persist widths. */
  readonly columnResize = output<{ key: string; width: number | null; widths: ColumnWidths }>();

  // --- derived ----------------------------------------------------------
  protected readonly snapshot = this.store.snapshot;
  protected readonly state = computed(() => this.snapshot().state);
  protected readonly resolvedColumns = computed(() => resolveColumns(this.columns()));
  protected readonly resolvedActions = computed(() => resolveActions(this.actions()));
  protected readonly resolvedInteraction = computed<Required<InteractionConfig>>(() => ({
    ...DEFAULT_INTERACTION,
    ...this.interaction(),
  }));
  protected readonly availableViews = computed<ExplorerView[]>(() => this.views() ?? ['list', 'grid']);
  protected readonly searchEnabled = computed(() => this.search() !== false);
  protected readonly searchScope = computed(() => {
    const search = this.search();
    return typeof search === 'object' ? search.scope ?? 'folder' : 'folder';
  });
  protected readonly searchPlaceholder = computed(() => {
    const search = this.search();
    return typeof search === 'object' && search.placeholder ? search.placeholder : 'Search';
  });
  protected readonly selectionConfig = computed(() => {
    // Read the snapshot so this recomputes when the config is pushed in.
    this.snapshot();
    return this.store.store.getSelectionConfig();
  });
  protected readonly hostClass = computed(() =>
    ['de-explorer', this.className()].filter(Boolean).join(' '),
  );
  protected readonly announcement = computed(() => {
    const snapshot = this.snapshot();
    if (snapshot.state.status !== 'idle') return '';
    const count = snapshot.items.length;
    if (snapshot.isSearching) {
      return `${count} ${count === 1 ? 'result' : 'results'} for ${snapshot.state.query}`;
    }
    const folder = snapshot.breadcrumbs[snapshot.breadcrumbs.length - 1]?.name ?? '';
    return `${folder}, ${count} ${count === 1 ? 'item' : 'items'}`;
  });

  constructor() {
    // Forward core events out as Angular outputs.
    this.store.store.setEvents({
      onFolderOpen: (folder) => this.folderOpen.emit(folder),
      onDocumentOpen: (item) => this.documentOpen.emit(item),
      onSelectionChange: (items) => this.selectionChange.emit(items),
      onSearch: (query) => this.searchChange.emit(query),
      onSort: (sort) => this.sortChange.emit(sort),
      onViewChange: (view) => this.viewChange.emit(view),
      onColumnResize: (key, width, widths) => this.columnResize.emit({ key, width, widths }),
    });

    effect(() => this.store.actions.setData(this.data(), this.mapper() as never));
    effect(() => this.store.configure({ rootLabel: this.rootLabel(), search: this.searchOptions() }));
    effect(() => this.store.actions.setSelectionConfig(this.selectionInput()));
    effect(() => this.store.actions.setView(this.view() ?? this.defaultView()));
    effect(() => {
      const widths = this.columnWidths();
      if (widths) this.store.actions.setColumnWidths(widths);
    });
    effect(() => {
      const sort = this.sort();
      if (sort) this.store.actions.setSort({ ...this.state().sort, ...sort });
    });
    effect(() => {
      if (this.loading()) this.store.actions.setStatus('loading');
      else if (this.error() !== undefined) this.store.actions.setStatus('error', this.error());
      else this.store.actions.setStatus('idle');
    });
  }

  private searchOptions(): SearchOptions & { scope?: 'folder' | 'global' } {
    const search = this.search();
    return typeof search === 'object' ? search : {};
  }

  private selectionInput(): Partial<SelectionConfig> {
    const selection = this.selection();
    if (typeof selection === 'boolean') return { enabled: selection };
    return { enabled: true, ...selection };
  }

  /**
   * Per-column bounds live in the ColumnDef, so they are applied here rather
   * than inside the store, which only knows about pixel numbers.
   */
  protected onColumnResize(event: { key: string; width: number }): void {
    const column = this.resolvedColumns().find((c) => c.key === event.key);
    this.store.actions.setColumnWidth(event.key, event.width, {
      ...(column?.minWidth !== undefined ? { min: column.minWidth } : {}),
      ...(column?.maxWidth !== undefined ? { max: column.maxWidth } : {}),
    });
  }

  protected onAction(event: { actionId: string; item: DocumentItem }): void {
    // Built-ins get their own outputs; everything reaches `action`.
    if (event.actionId === 'view') this.documentView.emit(event.item);
    if (event.actionId === 'download') this.documentDownload.emit(event.item);
    this.action.emit(event);
  }
}
